import { defineStore } from 'pinia';

/**
 * Pure UI-coordination state for header-level overlays — no domain data, mirroring
 * useTopBarStore.js's shape. Needed because the trigger row (Dropdown/Menu.vue,
 * Header/Mobile.vue), the Tutorials panel itself (mounted app-wide in app.vue), and
 * MobileNavBar.vue's slide-out account menu are three separate component trees with
 * no other way to reach each other.
 *
 * Also owns the account menu's open state (Dropdown/Menu.vue on desktop,
 * MobileNavBar.vue's slide-out on mobile) for the same reason: the Guided Walkthrough
 * setup that opens it (`onboarding-open-account-menu` in walkthroughSetups.js) lives
 * outside both component trees.
 *
 * Same reasoning for the carrier-profile side drawer's open state — it's local to
 * layouts/carrier-profile.vue, but `onboarding-open-profile-drawer` (walkthroughSetups.js)
 * needs to open it from outside that layout.
 */
export const useTutorialsPanelStore = defineStore('tutorialsPanelStore', {
  state: () => {
    return {
      isPanelOpen: false,
      // Incremented, never read as a boolean: MobileNavBar watches for a *change*,
      // not a value, since "close" has no natural opposite to toggle back from.
      mobileMenuCloseRequestId: 0,
      isDesktopAccountMenuOpen: false,
      isMobileAccountMenuOpen: false,
      isProfileDrawerOpen: false,
      // Topic id of the open Static Tutorial Modal, or null. Lives here for the same reason
      // as the rest: the trigger (Tutorials/Panel.vue, GetStarted/Widget.vue) and the modal
      // (mounted app-wide in app.vue) are separate component trees.
      activeStaticTutorialId: null,
    };
  },
  actions: {
    openPanel() {
      this.isPanelOpen = true;
    },
    closePanel() {
      this.isPanelOpen = false;
    },
    requestMobileMenuClose() {
      this.mobileMenuCloseRequestId += 1;
    },
    openAccountMenu() {
      this.isDesktopAccountMenuOpen = true;
      this.isMobileAccountMenuOpen = true;
    },
    closeAccountMenu() {
      this.isDesktopAccountMenuOpen = false;
      this.isMobileAccountMenuOpen = false;
    },
    openProfileDrawer() {
      this.isProfileDrawerOpen = true;
    },
    closeProfileDrawer() {
      this.isProfileDrawerOpen = false;
    },
    openStaticTutorial(topicId) {
      this.activeStaticTutorialId = topicId;
    },
    closeStaticTutorial() {
      this.activeStaticTutorialId = null;
    },
  },
});
